"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Cursor-following spotlight. Fills its nearest positioned ancestor and paints
 * a soft red radial glow that tracks the pointer, giving the hero an
 * interactive "stage light" feel. Listens on the window so the light keeps
 * tracking even while the cursor is over the headline or buttons, and springs
 * the position so it trails the cursor instead of snapping. Disabled (renders
 * nothing) for reduced motion and on touch — there is no hover pointer.
 */
export function Spotlight({
  className
}: {
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const opacity = useSpring(0, {
    stiffness: 200,
    damping: 30
  });
  const sx = useSpring(x, {
    stiffness: 150,
    damping: 25
  });
  const sy = useSpring(y, {
    stiffness: 150,
    damping: 25
  });
  const background = useMotionTemplate`radial-gradient(28rem 28rem at ${sx}px ${sy}px, rgba(220,38,38,0.18), transparent 70%)`;
  useEffect(() => {
    if (reduce) return;
    const handleMove = (event: globalThis.PointerEvent) => {
      if (event.pointerType === "touch") return;
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) {
        opacity.set(0);
        return;
      }
      x.set(event.clientX - rect.left);
      y.set(event.clientY - rect.top);
      opacity.set(1);
    };
    window.addEventListener("pointermove", handleMove, {
      passive: true
    });
    return () => window.removeEventListener("pointermove", handleMove);
  }, [reduce, x, y, opacity]);
  if (reduce) return null;
  return <div ref={ref} aria-hidden="true" className={cn("pointer-events-none absolute inset-0 -z-10 overflow-hidden", className)}>
      <motion.div className="absolute inset-0" style={{
      background,
      opacity
    }} />
    </div>;
}
type Blob = {
  className: string;
  drift: {
    x: number[];
    y: number[];
    scale: number[];
  };
  duration: number;
};
const BLOBS: Blob[] = [{
  className: "left-[8%] top-[6%] h-[26rem] w-[26rem] bg-red-600/20",
  drift: {
    x: [0, 40, -20, 0],
    y: [0, 30, -10, 0],
    scale: [1, 1.12, 0.95, 1]
  },
  duration: 22
}, {
  className: "right-[6%] top-[24%] h-[22rem] w-[22rem] bg-rose-500/15",
  drift: {
    x: [0, -30, 25, 0],
    y: [0, 20, 40, 0],
    scale: [1, 0.92, 1.1, 1]
  },
  duration: 26
}, {
  className: "left-[40%] top-[50%] h-[30rem] w-[30rem] bg-red-700/15",
  drift: {
    x: [0, 25, -35, 0],
    y: [0, -25, 15, 0],
    scale: [1, 1.08, 0.9, 1]
  },
  duration: 30
}];

/**
 * Ambient "aurora" — a few large, heavily blurred red blobs that drift and
 * breathe behind the content. Pure decoration; collapses to a single static
 * glow under reduced motion.
 */
export function Aurora(t0) {
  const $ = _c(7);
  const {
    className
  } = t0;
  const reduce = useReducedMotion();
  let t1;
  if ($[0] !== className) {
    t1 = cn("pointer-events-none absolute inset-0 -z-10 overflow-hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reduce) {
    t2 = BLOBS.map((blob, index) => reduce ? <div key={index} className={cn("absolute rounded-full blur-[7rem]", blob.className)} /> : <motion.div key={index} className={cn("absolute rounded-full blur-[7rem]", blob.className)} animate={{
      x: blob.drift.x,
      y: blob.drift.y,
      scale: blob.drift.scale
    }} transition={{
      duration: blob.duration,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut"
    }} />);
    $[2] = reduce;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div aria-hidden="true" className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * A slowly breathing radial glow. Thin client wrapper so the surrounding copy
 * can stay in a server component. Pass the gradient/positioning via className.
 */
export function BreathingGlow(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  const reduce = useReducedMotion();
  let t1;
  if ($[0] !== reduce) {
    t1 = reduce ? undefined : {
      opacity: [0.6, 1, 0.6],
      scale: [1, 1.08, 1]
    };
    $[0] = reduce;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      duration: 7,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== t1) {
    t3 = <motion.div aria-hidden="true" className={className} animate={t1} transition={t2} />;
    $[3] = className;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Wraps a run of text in an animated red gradient sheen that periodically
 * sweeps across it. The text itself is passed as children, so it can be
 * rendered by a server component (keeping the copy in the initial payload).
 * Falls back to flat red text under reduced motion.
 */
export function Sheen(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const reduce = useReducedMotion();
  if (reduce) {
    let t1;
    if ($[0] !== children) {
      t1 = <span className="text-red-500">{children}</span>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      backgroundPosition: ["250% 0%", "-50% 0%"]
    };
    t2 = {
      duration: 2.4,
      ease: "easeInOut",
      repeat: Infinity,
      repeatDelay: 4
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <motion.span className="bg-[linear-gradient(110deg,#ef4444_25%,#fecaca_50%,#ef4444_75%)] bg-[length:250%_100%] bg-clip-text text-transparent" animate={t1} transition={t2}>{children}</motion.span>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}